import { call, put, takeLatest, select } from 'redux-saga/effects';
import { IAppState } from '@/app-state';
import * as Actions from './actions';
import * as Apis from './apis';
import * as ApiModel from '@/app/common/conversation/interfaces/api-model';
import { EntityType } from '@/app-constants';
import { removeChangeRequestsItemUnread, CHANGE_REQUESTS_LIST_ACTIONS, SUPPLIER_CONFIRM_CHANGE_REQUEST, removeChangeRequestFromState } from '@/app/areas/unacknowledged-po/details/components/change-requests/change-requests-actions';
import { removeHoldRequestsItemUnread } from '@/app/areas/unacknowledged-po/details/components/hold-requests/hold-requests-actions';

// #region Selects

export const getConversationId = (state: IAppState): number => (state.conversationState.conversationId);

export const getCodesFromDetailState = (state: IAppState): { customerCode: string, supplierCode: string; } => ({
  customerCode: state.poDetailsState.customerCode,
  supplierCode: state.poDetailsState.supplierCode
});

export const isActiveConversationPoLevel = (state: IAppState): boolean => state.conversationState.conversationId === state.poDetailsState.conversationId || state.conversationState.conversationId === 0;

export const changeRequestHasUnreadMessages = (changeRequestId: number) => ({ poItemChangeRequestsState }: IAppState): boolean => {
  return poItemChangeRequestsState.changeRequests.filter(cr => cr.changeRequestId === changeRequestId && cr.hasUnreadMessages).length > 0;
};

export const holdRequestHasUnreadMessages = (holdRequestId: number) => ({ poItemHoldRequestsState }: IAppState): boolean => {
  return poItemHoldRequestsState.holdRequests.filter(hr => hr.id === holdRequestId && hr.hasUnreadMessages).length > 0;
};

// #endregion

export function* getConversationSaga(action: ReturnType<typeof Actions.conversationRequestAction>) {
  try {
    const conversationId = action.payload.conversationId;
    if (conversationId) {
      const codes = yield select(getCodesFromDetailState);
      const request: ApiModel.IGetMessagesRequest = {
        ...codes,
        conversationId,
      };
      const response = yield call(Apis.getConversation, request);

      yield put(Actions.conversationSuccessAction({
        messages: response.messages,
        entity: {
          entityId: action.payload.entityId,
          entityType: action.payload.entityType
        }
      }));
    } else {
      yield put(Actions.conversationSuccessAction({
        entity: {
          entityId: 0,
          entityType: EntityType.PurchaseOrder
        },
        messages: []
      }));
    }
  } catch (error) {
    yield put(Actions.conversationFailureAction(error));
  }
}

export function* postConversationMessageSaga(action: ReturnType<typeof Actions.conversationPostAction>) {
  try {
    const { customerCode, supplierCode } = yield select(getCodesFromDetailState);
    const request: ApiModel.ICreateMessageRequest = {
      entityId: action.request.entityId,
      entityType: action.request.entityType,
      conversationId: action.request.conversationId,
      customerCode,
      supplierCode,
      content: action.request.message
    };
    yield call(Apis.postConversationMessage, request);

    yield put(Actions.conversationRequestAction({
      conversationId: action.request.conversationId,
      entityId: action.request.entityId,
      entityType: action.request.entityType
    }));
  } catch (error) {
    yield put(Actions.conversationFailureAction(error));
  }
}

export function* postCreateConversationSaga(action: ReturnType<typeof Actions.createConversationPostAction>) {
  const codes = yield select(getCodesFromDetailState);
  try {
    const request: ApiModel.IPostPurchaseOrderConversationRequest = {
      ...codes,
      purchaseOrderId: action.request.entityId,
      content: action.request.message
    };
    const response: ApiModel.IPostPurchaseOrderConversationResponse = yield call(Apis.createConversation, request);
    yield put(Actions.createConversationSuccessAction(response.conversationId));
    yield put(Actions.conversationRequestAction({
      conversationId: response.conversationId,
      entityId: action.request.entityId,
      entityType: EntityType.PurchaseOrder,
    }));
  } catch (error) {
    yield put(Actions.conversationFailureAction(error));
  }
}

export function* getNotesSaga(payload) {
  try {
    const notesResponse = yield call(Apis.getNotes, payload.request);
    yield put(Actions.notesRequestSuccessAction(notesResponse));
  } catch (error) {
    yield put(Actions.notesFailureAction(error));
  }
}

export function* postNoteSaga(action) {
  try {
    yield call(Apis.createNote, action.payload);
    yield put(Actions.notePostSuccess());
  }
  catch (error) {
    yield put(Actions.notesFailureAction(error));
  }
}

export function* handleCancelConfirmSaga(action) {
  const conversationIsPoLevel: boolean = yield select(isActiveConversationPoLevel);
  if (!conversationIsPoLevel) {
    yield put(Actions.conversationHideAction());
  }

  const changeRequestHasUnread = yield select(changeRequestHasUnreadMessages(action.payload.changeRequestId));
  if (changeRequestHasUnread) {
    yield put(removeChangeRequestsItemUnread({ changeRequestId: action.payload.changeRequestId }));
  }

  yield put(removeChangeRequestFromState({ changeRequestId: action.payload.changeRequestId }));
}

export function* deleteMessageRecipient(action: ReturnType<typeof Actions.conversationSuccessAction>) {
  const codes = yield select(getCodesFromDetailState);
  const conversationId = yield select(getConversationId);
  const request: ApiModel.IDeleteMessageRecipientRequest = {
    conversationId,
    ...codes,
    ...action.payload.entity
  };
  yield call(Apis.deleteMessageRecipient, request);

  if (request.entityType === EntityType.HoldRequest) {
    const hasUnread = yield select(holdRequestHasUnreadMessages(request.entityId));
    if (hasUnread) {
      yield put(removeHoldRequestsItemUnread({ holdRequestId: request.entityId }));
    }
  }
  else if (request.entityType === EntityType.ChangeRequest) {
    const hasUnread = yield select(changeRequestHasUnreadMessages(request.entityId));
    if (hasUnread) {
      yield put(removeChangeRequestsItemUnread({ changeRequestId: request.entityId }));
    }
  }
}

export function* watchGetConversationSaga() {
  yield takeLatest(Actions.CONVERSATION_ACTIONS.REQUEST, getConversationSaga);
}

export function* watchPostConversationMessageSaga() {
  yield takeLatest(Actions.CONVERSATION_ACTIONS.POST, postConversationMessageSaga);
}

export function* watchPostCreateConversationSaga() {
  yield takeLatest(Actions.CREATE_CONVERSATION_ACTIONS.POST, postCreateConversationSaga);
}
export function* watchGetNotesSaga() {
  yield takeLatest(Actions.CONVERSATION_ACTIONS.NOTES_REQUEST, getNotesSaga);
}

export function* watchPostNoteSaga() {
  yield takeLatest(Actions.CONVERSATION_ACTIONS.NOTES_POST, postNoteSaga);
}

export function* watchDeleteMessageRecipient() {
  yield takeLatest(Actions.CONVERSATION_ACTIONS.SUCCESS, deleteMessageRecipient);
}

export function* watchCancelConfirmSaga() {
  yield takeLatest([CHANGE_REQUESTS_LIST_ACTIONS.CANCELED, SUPPLIER_CONFIRM_CHANGE_REQUEST.SUCCESS], handleCancelConfirmSaga);
}
